import { useState, useEffect, useCallback } from "@wordpress/element";
import axe from "axe-core";

const RESULTS_KEY = "accessibilityScanResults";
const RESOLVED_MAP_KEY = "resolvedIssueMap";

function readJSON(key, fallback) {
	try {
		return JSON.parse(localStorage.getItem(key)) || fallback;
	} catch {
		return fallback;
	}
}

// Accept storage dependency injection so we don't create a second storage instance.
// Pass an object containing { upsertRecord, updateResolvedForUrl } from provider.
export default function useScanResults({
	upsertRecord,
	updateResolvedForUrl,
} = {}) {
	if (!upsertRecord || !updateResolvedForUrl) {
		console.warn(
			"useScanResults called without storage deps; resolved counts may desync.",
		);
	}
	const [issues, setIssues] = useState([]);
	const [resolvedIssues, setResolvedIssues] = useState([]);
	const [isScanning, setIsScanning] = useState(false);
	const [scanError, setScanError] = useState(null);

	const currentUrl = window.location.href.split("?")[0];

	const loadExisting = useCallback(() => {
		const stored = readJSON(RESULTS_KEY, []);
		setIssues(stored);
		const resolvedMap = readJSON(RESOLVED_MAP_KEY, {});
		setResolvedIssues(resolvedMap[currentUrl] || []);
	}, [currentUrl]);

	useEffect(() => {
		loadExisting();
	}, [loadExisting]);

	const runScan = useCallback(() => {
		setIsScanning(true);
		setScanError(null);

		// Configure axe to exclude specific elements
		const axeOptions = {
			exclude: [
				// Exclude accessibility widget elements
				["#accessibility-widget"],
				["#accessibility-widgetc"],
				["#accessibility-widget-parent"],
				["#zn-sheet-widget"],
				["#accessibility-assistant-sheet"],
				// Exclude elements with data-radix-popper-content-wrapper attribute
				["[data-radix-popper-content-wrapper]"],
			],
		};

		axe.run(document, axeOptions, (err, results) => {
			if (err) {
				setScanError(err.message || "Scan failed");
				setIsScanning(false);
				return;
			}
			const violations = results.violations.map((v) => ({
				id: v.id,
				help: v.help,
				tags: v.tags,
				nodes: v.nodes.map((n) => ({ target: n.target, html: n.html })),
			}));
			try {
				localStorage.setItem(RESULTS_KEY, JSON.stringify(violations));
			} catch {}
			setIssues(violations);

			// Reset resolved map for this URL since it's a fresh scan
			try {
				const map = readJSON(RESOLVED_MAP_KEY, {});
				if (map[currentUrl]) {
					delete map[currentUrl];
					localStorage.setItem(RESOLVED_MAP_KEY, JSON.stringify(map));
				}
			} catch {}

			const levelA = violations.filter((v) =>
				v.tags?.includes("wcag-a"),
			).length;
			const levelAA = violations.filter((v) =>
				v.tags?.includes("wcag-aa"),
			).length;
			const levelAAA = violations.filter((v) =>
				v.tags?.includes("wcag-aaa"),
			).length;
			upsertRecord({
				title: document.title,
				url: currentUrl,
				date: new Date().toISOString(),
				totalIssues: violations.length,
				issuesResolved: 0,
				levelA,
				levelAA,
				levelAAA,
			});
			setResolvedIssues([]);
			setIsScanning(false);
		});
	}, [currentUrl, upsertRecord]);

	const markResolved = useCallback(
		(issueId) => {
			setResolvedIssues((prev) => {
				if (prev.includes(issueId)) return prev;
				const updated = [...prev, issueId];
				// Persist map
				const map = readJSON(RESOLVED_MAP_KEY, {});
				map[currentUrl] = updated;
				try {
					localStorage.setItem(RESOLVED_MAP_KEY, JSON.stringify(map));
				} catch {}

				// Derive per-level resolved counts
				const issueById = issues.reduce((m, it) => {
					m[it.id] = it;
					return m;
				}, {});
				let resA = 0,
					resAA = 0,
					resAAA = 0;
				updated.forEach((id) => {
					const it = issueById[id];
					if (!it) return;
					if (it.tags?.includes("wcag-a")) resA += 1;
					if (it.tags?.includes("wcag-aa")) resAA += 1;
					if (it.tags?.includes("wcag-aaa")) resAAA += 1;
				});
				updateResolvedForUrl(currentUrl, {
					total: updated.length,
					A: resA,
					AA: resAA,
					AAA: resAAA,
				});
				return updated;
			});
		},
		[currentUrl, updateResolvedForUrl, issues],
	);

	const progress = issues.length
		? (resolvedIssues.length / issues.length) * 100
		: 0;

	return {
		issues,
		resolvedIssues,
		isScanning,
		scanError,
		runScan,
		markResolved,
		progress,
		reload: loadExisting,
	};
}
